import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  computed,
  EventEmitter,
  input,
  Input,
  Output,
} from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import {
  ENTRY_ID,
  HeaderCell,
  MappedTableData,
  PaginationPageSize,
  SortDirections,
  TableRow,
} from '@markant-platform/shared/table/model';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@jsverse/transloco';
import { TranslocoDatePipe } from '@jsverse/transloco-locale';
import { CommonModule } from '@angular/common';
import { MarkantElementsModule } from '@markant/elements-angular';
import { PaginationFooterComponent } from '../pagination-footer/pagination-footer.component';
import { DateFormat } from '@markant-platform/shared/util-helpers';
import { DefaultValuePipe } from '@markant-platform/shared/util-pipes';

const DEFAULT_TABLE_DATA: MappedTableData = {
  showCheckBoxes: false,
  showRadioButtons: false,
  sortColumnIds: [],
  sortStartColumnId: '',
  sortStartDirection: 'asc',
  headerCells: [],
  tableRows: [],
};

@Component({
  selector: 'markant-platform-table',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MarkantElementsModule, TranslocoModule, PaginationFooterComponent, DefaultValuePipe],
  templateUrl: './table.component.html',
  styleUrls: ['./table.component.scss'],
  providers: [TranslocoDatePipe],
})
export class TableComponent {
  defaultHeaderCells = input.required<HeaderCell[]>();
  defaultHiddenCells = input<string[] | undefined>([]);

  visibleCells = computed<HeaderCell[]>(() =>
    this.defaultHeaderCells().filter(
      ({ headerCellColumnId }) => !this.defaultHiddenCells()?.includes(headerCellColumnId)
    )
  );

  @Input() loading = false;
  @Input() totalCount = 0;
  @Input() pageSize: PaginationPageSize = 25;
  @Input() pageIndex = 0;
  @Input() showPagination = true;
  @Input() stickyHeader = false;

  private goToBlocked = false;

  @Input() set preselected(preselectedValues: Map<string, number> | undefined) {
    if (preselectedValues) {
      this.selectedIds = new Map();
      preselectedValues.forEach((value, key) => this.selectedIds.set(key, value));
      this.allRowsOnPageSelected();
    }
  }

  @Input() set resetSelection(reset: boolean | undefined) {
    if (reset) {
      this.selectedIds = new Map();
    }
  }

  @Input() set data(data: MappedTableData | null) {
    const tableData: MappedTableData = data ?? DEFAULT_TABLE_DATA;

    this._data = this.setTableCellContent(tableData);
    this.allRowsOnPageSelected();
    this.sortColumnId$.next(tableData.sortStartColumnId);
    this.cdr.detectChanges();
  }

  get data(): MappedTableData {
    return this._data;
  }

  @Output() pageSizeChanged = new EventEmitter<PaginationPageSize>();
  @Output() indexChanged = new EventEmitter<number>();
  @Output() sortChanged = new EventEmitter<{ columnId: string; sortDirection: SortDirections }>();
  @Output() selectionChanged = new EventEmitter<Map<string, number>>();
  @Output() rowClicked = new EventEmitter<TableRow>();
  @Output() checkboxSelected = new EventEmitter<string>();
  @Output() radioButtonSelected = new EventEmitter<string>();

  sortColumnId$ = new BehaviorSubject<string>('');
  selectedIds: Map<string, number> = new Map();
  selectedId = '';
  _data!: MappedTableData;

  constructor(
    private readonly router: Router,
    private readonly route: ActivatedRoute,
    private readonly translocoService: TranslocoService,
    private readonly dateTime: TranslocoDatePipe,
    private readonly cdr: ChangeDetectorRef
  ) {}

  isDate(value: string | Date): boolean {
    return value instanceof Date;
  }

  onIndexChange(index: number): void {
    this.indexChanged.emit(index);
  }

  onPageSizeChange(paginationPageSize: PaginationPageSize): void {
    this.pageSizeChanged.emit(paginationPageSize);
  }

  onSortChange(sortData: { columnId: string; sortDirection: string }): void {
    const { columnId, sortDirection } = sortData;
    const [firstColumn] = this._data.sortColumnIds;
    const updatedColumnId = columnId ?? firstColumn;

    this.sortColumnId$.next(updatedColumnId);
    this.sortChanged.emit({
      columnId: updatedColumnId,
      sortDirection: (sortDirection as SortDirections) ?? 'none',
    });
  }

  goTo(tableRow: TableRow): void {
    const { entryId, rowLink, disabledRow } = tableRow;

    if (!disabledRow) {
      if (!this.goToBlocked && !this._data.rowClickControl) {
        const { disabledRowLink } = this.data;
        let routeArr = this.data.rowLink;

        //Disabled table row click and redirect
        if (disabledRowLink) {
          return;
        }

        if (rowLink) {
          routeArr = [rowLink];
        } else if (routeArr) {
          routeArr[routeArr.indexOf(ENTRY_ID)] = entryId; // replace ENTRY_ID constant with real entry id if rowLink is defined.
        } else {
          routeArr = ['./', `${entryId}`];
        }

        this.router.navigate(routeArr, { relativeTo: this.route });
      } else {
        this.rowClicked.emit(tableRow);
      }
    }
  }

  setTableCellContent(data: MappedTableData): MappedTableData {
    const headerCells = data.headerCells.map(headerCell => {
      return {
        ...headerCell,
        isSortable: data.sortColumnIds.includes(headerCell.headerCellColumnId),
        sortDirection:
          data.sortStartColumnId === headerCell.headerCellColumnId
            ? (data.sortStartDirection as 'asc' | 'desc' | 'none')
            : 'none',
      };
    });

    const tableRows = data.tableRows.map(tableRow => {
      const tableCells = tableRow.tableCells.map(tableCell => {
        let label = '';

        const contentArray = tableCell.content?.map((item: string | Date) => {
          const dateFormat = tableCell.dateFormat ? DateFormat[tableCell.dateFormat] : undefined;
          const stringOrDateContent = this.isDate(item) ? this.dateTime.transform(item, dateFormat) : item;
          const content =
            tableCell.contentIsTranslationKey && item !== '-'
              ? this.translocoService.translate(item as string)
              : stringOrDateContent;
          label = tableCell.actionElements?.at(0)
            ? this.translocoService.translate(tableCell.actionElements?.at(0)?.label || '')
            : label + content;
          return content || '-';
        });

        return { ...tableCell, content: contentArray, label: label };
      });

      return { ...tableRow, tableCells: tableCells };
    });

    return { ...data, headerCells: headerCells, tableRows: tableRows };
  }

  toggleSelect(entryId: string, version: number, selectionPrefix = ''): void {
    const lookupName = selectionPrefix + entryId;
    this.selectedIds.has(lookupName) ? this.selectedIds.delete(lookupName) : this.selectedIds.set(lookupName, version);
    this.selectionChanged.emit(this.selectedIds);
  }

  toggleAllSelections(isSelected: boolean): void {
    this.data.tableRows
      .filter(({ entryId }) => isSelected !== this.selectedIds.has(entryId))
      .forEach(({ entryId, version }) => this.forceToggle(entryId, version, isSelected));

    this.selectionChanged.emit(this.selectedIds);
  }

  forceToggle(entryId: string, version: number, isSelect: boolean): void {
    const hasEntryId: boolean = this.selectedIds.has(entryId);

    if (isSelect && !hasEntryId) {
      this.selectedIds.set(entryId, version);
    } else if (!isSelect && hasEntryId) {
      this.selectedIds.delete(entryId);
    }
  }

  forceToggleOff(entryId: string): void {
    this.selectedIds.delete(entryId);
    this.selectionChanged.emit(this.selectedIds);
  }

  allRowsOnPageSelected(): boolean {
    if (this.data) {
      const { tableRows } = this.data;

      if (tableRows.length) {
        return tableRows.every(({ entryId }) => this.selectedIds.has(entryId));
      }
    }

    return false;
  }

  clearRowSelection(): void {
    this.selectedIds.clear();
  }

  functionCaller(
    callbackFunction: (...args: unknown[]) => unknown,
    object: unknown,
    functionParam: (string | Date)[] = []
  ): void {
    this.goToBlocked = true;
    const convertedFunctionParam: string = this.isDate(functionParam[0])
      ? functionParam[0].toString()
      : (functionParam[0] as string);
    if (convertedFunctionParam === undefined) {
      callbackFunction(object);
      setTimeout(() => (this.goToBlocked = false));
      return;
    }
    callbackFunction(convertedFunctionParam);
    setTimeout(() => (this.goToBlocked = false));
  }

  onClickCheckbox(event: MouseEvent, entryId: string): void {
    event.stopPropagation();
    this.checkboxSelected.emit(entryId);
  }

  onClickRadioButton(event: MouseEvent, entryId: string): void {
    event.stopPropagation();
    this.selectedId = entryId;
    this.radioButtonSelected.emit(entryId);
  }
}
